import React, {
  createContext,
  useContext,
  useRef,
  useEffect,
  useCallback,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import onScan from 'onscan.js';
import { getBarcodeScanningEnabled } from 'lib/selectors/getBarcodeScanningEnabled';

const BarcodeScannerContext = createContext(null);

const DEFAULT_BARCODE_CONFIG = {
  reactToPaste: false, // Don't react to paste events
  minLength: 3, // Minimum barcode length (prevents accidental single-key scans)
  suffixKeyCodes: [13], // Enter key (most common scanner suffix)
  avgTimeByChar: 30, // Average time between characters for scan detection (ms)
  timeBeforeScanTest: 100, // Wait time before testing if it's a scan
  ignoreIfFocusOn: ['input', 'textarea', 'select'], // Don't trigger scans when typing in form fields
};

/**
 * BarcodeScannerProvider
 *
 * Provides a single global barcode scanner instance with priority-based handler routing.
 * Eliminates race conditions from multiple scanner instances by maintaining a registry
 * of handlers and routing scans to the highest priority active handler.
 *
 * @example
 * <BarcodeScannerProvider>
 *   <App />
 * </BarcodeScannerProvider>
 */
export const BarcodeScannerProvider = ({ children }) => {
  const globalIsEnabled = useSelector(getBarcodeScanningEnabled);
  const handlersRef = useRef(new Map()); // Map<handlerId, { onScan, priority, scanOptions }>
  const isAttachedRef = useRef(false);
  const scanOptionsRef = useRef(DEFAULT_BARCODE_CONFIG);

  /**
   * Get the active handler (highest priority)
   * @returns {Object|null} Handler object or null if no handlers
   */
  const getActiveHandler = useCallback(() => {
    if (handlersRef.current.size === 0) {
      return null;
    }

    const handlers = Array.from(handlersRef.current.entries());
    handlers.sort((a, b) => {
      return b[1].priority - a[1].priority;
    });

    return handlers[0][1];
  }, []);

  /**
   * Route scan to active handler
   * @param {string} barcode - Scanned barcode value
   */
  const handleScan = useCallback(
    (barcode) => {
      const activeHandler = getActiveHandler();
      if (activeHandler?.onScan) {
        activeHandler.onScan(barcode);
      }
    },
    [getActiveHandler]
  );

  /**
   * Get merged scan options from active handler
   * @returns {Object} Merged scan options
   */
  const getScanOptions = useCallback(() => {
    const activeHandler = getActiveHandler();
    const merged = {
      ...DEFAULT_BARCODE_CONFIG,
      ...activeHandler?.scanOptions,
      onScan: handleScan,
      onScanError: (error) => {
        // eslint-disable-next-line no-console
        console.error('[BarcodeScannerProvider] Scan error:', error);
      },
    };

    scanOptionsRef.current = merged;
    return merged;
  }, [getActiveHandler, handleScan]);

  /**
   * Update scanner with current options
   */
  const updateScanner = useCallback(() => {
    if (!globalIsEnabled || handlersRef.current.size === 0) {
      return;
    }

    try {
      const scanOptions = getScanOptions();

      if (isAttachedRef.current) {
        onScan.detachFrom(document);
      }

      onScan.attachTo(document, scanOptions);
      isAttachedRef.current = true;
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(
        '[BarcodeScannerProvider] Failed to update scanner:',
        error
      );
    }
  }, [globalIsEnabled, getScanOptions]);

  /**
   * Register a barcode scan handler with priority
   *
   * @param {string} handlerId - Unique identifier for this handler
   * @param {Function} callback - Callback function when barcode is scanned
   * @param {Object} options - Handler configuration
   * @param {number} options.priority - Handler priority (higher = more important)
   * @param {Object} options.scanOptions - onScan.js configuration options
   * @returns {Function} Unregister function
   */
  const registerHandler = useCallback(
    (handlerId, callback, options = {}) => {
      const { priority = 0, scanOptions = {} } = options;

      handlersRef.current.set(handlerId, {
        onScan: callback,
        priority,
        scanOptions,
      });

      const activeHandler = getActiveHandler();
      if (activeHandler) {
        updateScanner();
      }

      return () => {
        handlersRef.current.delete(handlerId);

        if (handlersRef.current.size > 0) {
          updateScanner();
        }
      };
    },
    [getActiveHandler, updateScanner]
  );

  // Initialize/update scanner when handlers or settings change
  useEffect(() => {
    if (!globalIsEnabled || handlersRef.current.size === 0) {
      if (isAttachedRef.current) {
        try {
          onScan.detachFrom(document);
          isAttachedRef.current = false;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[BarcodeScannerProvider] Failed to detach:', error);
        }
      }
      return;
    }

    updateScanner();
  }, [globalIsEnabled, updateScanner]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (isAttachedRef.current) {
        try {
          onScan.detachFrom(document);
          isAttachedRef.current = false;
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[BarcodeScannerProvider] Cleanup failed:', error);
        }
      }
    };
  }, []);

  const value = useMemo(() => {
    return {
      registerHandler,
      isEnabled: globalIsEnabled,
    };
  }, [registerHandler, globalIsEnabled]);

  return (
    <BarcodeScannerContext.Provider value={value}>
      {children}
    </BarcodeScannerContext.Provider>
  );
};

BarcodeScannerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

/**
 * Hook to access barcode scanner context
 * @returns {Object} Context value with registerHandler and isEnabled
 * @throws {Error} If used outside BarcodeScannerProvider
 */
export const useBarcodeScanner = () => {
  const context = useContext(BarcodeScannerContext);
  if (!context) {
    throw new Error(
      'useBarcodeScanner must be used within BarcodeScannerProvider'
    );
  }
  return context;
};
